'use client';

import type React from 'react';
import { Dispatch, SetStateAction, useState } from 'react';
import { Checkbox } from 'src/components/Checkbox';
import { InputText } from 'src/components/InputText';
import { GradeForm } from 'src/features/createProduct/screens/CreateProduct/components/GradeForm';
import {
  FormInputGradeSize,
  FormRegisterProduct,
} from 'src/features/createProduct/types/types';
import useForm from 'src/hooks/useForm';
import type { Grade } from 'src/model/Grade';
import type { GradeSize } from 'src/model/GradeSize';
import { api } from 'src/services/api';
import { EditGradeSize } from './editGradeSize';
import { Package, DollarSign, Layers, Ruler } from 'lucide-react';
import { toast } from 'react-toastify';

interface ValuesVariationsProps {
  product: any;
  formErrors: any;
  isGrade: boolean;
  grade: Grade[];
  setGrade: Dispatch<SetStateAction<Grade[]>>;
  onChangeFormInput: (inputName: string) => (value: string) => void;
  addQuantiValue: (e: React.FormEvent) => void;
  removeGrade: (index: string) => void;
  addSize: (e: React.FormEvent) => void;
  onGrade: (inputName: string) => (value: string) => void;
  onChangeFormFileInputGradeSize: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  filesImageGrade: File[];
  previewFilesGrade: string;
  previewGradesSizes: any[];
}

const ValuesVariations: React.FC<ValuesVariationsProps> = ({
  product,
  formErrors,
  isGrade,
  grade,
  setGrade,
  onChangeFormInput,
  addQuantiValue,
  removeGrade,
  addSize,
  onGrade,
  onChangeFormFileInputGradeSize,
  filesImageGrade,
  previewFilesGrade,
  previewGradesSizes,
}) => {
  const [isEditGradeSize, setIsEditGradeSize] = useState<boolean>(false);
  const [editingGradeSize, setEditingGradeSize] = useState<{
    gradeId: string;
    gradeSizeId: string;
    gradeIndex: number;
    sizeIndex: number;
  } | null>(null);

  const {
    formData: formDataEditGradeSize,
    formErrors: formErrorsEditGradeSize,
    onChangeFormInput: onChangeFormInputEditGradeSize,
    resetForm: resetFormEditGradeSize,
  } = useForm({
    initialData: { size: '', stock: '', price: '', discount: '' },
  });

  const handleEditGradeSize = (
    gradeSize: GradeSize,
    gradeSizeId: string,
    gradeId: string
  ) => {
    // Encontrar índices
    const gradeIndex = grade.findIndex((g) => g.id === gradeId);
    const sizeIndex =
      grade[gradeIndex]?.gradeSizes?.findIndex((s) => s.id === gradeSizeId) ??
      -1;

    if (gradeIndex === -1 || sizeIndex === -1) {
      toast.error('Erro ao localizar tamanho para edição');
      return;
    }

    // Configurar estado de edição
    setEditingGradeSize({
      gradeId,
      gradeSizeId,
      gradeIndex,
      sizeIndex,
    });

    // Preencher formulário
    onChangeFormInputEditGradeSize(FormInputGradeSize.size)(gradeSize.size);
    onChangeFormInputEditGradeSize(FormInputGradeSize.stock)(
      String(gradeSize.stock)
    );
    onChangeFormInputEditGradeSize(FormInputGradeSize.price)(
      String(gradeSize.price.price)
    );
    onChangeFormInputEditGradeSize(FormInputGradeSize.discount)(
      String(gradeSize.price.discount)
    );

    setIsEditGradeSize(true);
  };

  const handleOnclose = () => {
    setIsEditGradeSize(false);
    setEditingGradeSize(null);
    resetFormEditGradeSize();
  };

  // CORREÇÃO: Agora só atualiza o estado local e fecha o modal
  const handleOnSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!editingGradeSize) {
      toast.error('Erro: informações de edição não encontradas');
      return;
    }

    // Validação básica
    if (!formDataEditGradeSize[FormInputGradeSize.size]?.trim()) {
      toast.error('O tamanho é obrigatório');
      return;
    }

    if (!formDataEditGradeSize[FormInputGradeSize.price]) {
      toast.error('O preço é obrigatório');
      return;
    }

    if (!formDataEditGradeSize[FormInputGradeSize.stock]) {
      toast.error('O estoque é obrigatório');
      return;
    }

    // CORREÇÃO: Atualizar apenas o estado local, SEM chamar a API
    setGrade((prevGrades) => {
      const updatedGrades = [...prevGrades];
      const targetGrade = { ...updatedGrades[editingGradeSize.gradeIndex] };

      if (targetGrade.gradeSizes) {
        targetGrade.gradeSizes = [...targetGrade.gradeSizes];

        targetGrade.gradeSizes[editingGradeSize.sizeIndex] = {
          ...targetGrade.gradeSizes[editingGradeSize.sizeIndex],
          size: formDataEditGradeSize[FormInputGradeSize.size],
          stock: formDataEditGradeSize[FormInputGradeSize.stock],
          price: {
            price: Number(formDataEditGradeSize[FormInputGradeSize.price]),
            discount: Number(
              formDataEditGradeSize[FormInputGradeSize.discount] || 0
            ),
          },
        };
      }

      updatedGrades[editingGradeSize.gradeIndex] = targetGrade;
      return updatedGrades;
    });

    toast.success(
      'Tamanho atualizado! Clique em "Salvar Alterações" para confirmar.'
    );

    // Fechar modal
    handleOnclose();
  };

  return (
    <div className="">
      <div className="space-y-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-red-100 to-red-50 border border-red-200">
            <Layers className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-red-500 bg-clip-text text-transparent">
            Preços e Variações
          </h2>
        </div>
        <p className="text-muted-foreground text-base ml-[60px]">
          Configure os valores e variações do produto
        </p>
      </div>

      <div className="bg-card rounded-2xl border-2 border-border shadow-lg hover:shadow-xl transition-shadow duration-300">
        <div className="p-8 space-y-8">
          <div className="mt-6">
            {isGrade ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-1 w-1 rounded-full bg-red-600" />
                  <h3 className="text-lg font-semibold text-foreground">
                    Gerenciar Variações
                  </h3>
                </div>
                <GradeForm
                  formData={product}
                  formErrors={formErrors}
                  variants={grade}
                  onChangeFormInput={onChangeFormInput}
                  addVariant={addQuantiValue}
                  removeVariant={removeGrade}
                  addSize={addSize}
                  onChangeFormFileInput={onChangeFormFileInputGradeSize}
                  filesImageGrade={filesImageGrade}
                  previewFilesGrade={previewFilesGrade}
                  previewGradesSizes={previewGradesSizes}
                  onEditGradeSize={handleEditGradeSize}
                  isEdit={true}
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-1 w-1 rounded-full bg-red-600" />
                  <h3 className="text-lg font-semibold text-foreground">
                    Informações de Preço e Estoque
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <InputText
                      label="Preço"
                      name="price"
                      type="number"
                      id="price"
                      value={product[FormInputGradeSize.price]}
                      onChange={(e) =>
                        onChangeFormInput(FormInputGradeSize.price)(
                          e.target.value
                        )
                      }
                      error={formErrors.price?.[0]}
                      inputClassName="rounded-xl px-4 py-3 w-full bg-background border-2 border-input focus:border-red-500 focus:ring-4 focus:ring-red-500/10 transition-all duration-300 outline-none"
                      placeholder="R$ 0,00"
                    />
                  </div>
                  <div className="space-y-2">
                    <InputText
                      label="Desconto (%)"
                      name="discount"
                      type="number"
                      id="discount"
                      value={product[FormInputGradeSize.discount]}
                      onChange={(e) =>
                        onChangeFormInput(FormInputGradeSize.discount)(
                          e.target.value
                        )
                      }
                      error={formErrors.discount?.[0]}
                      inputClassName="rounded-xl px-4 py-3 w-full bg-background border-2 border-input focus:border-red-500 focus:ring-4 focus:ring-red-500/10 transition-all duration-300 outline-none"
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <InputText
                      label="Quantidade em Estoque"
                      name="stock"
                      type="number"
                      id="stock"
                      value={product[FormInputGradeSize.stock]}
                      onChange={(e) =>
                        onChangeFormInput(FormInputGradeSize.stock)(
                          e.target.value
                        )
                      }
                      error={formErrors.stock?.[0]}
                      inputClassName="rounded-xl px-4 py-3 w-full bg-background border-2 border-input focus:border-red-500 focus:ring-4 focus:ring-red-500/10 transition-all duration-300 outline-none"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {isEditGradeSize && editingGradeSize && (
        <div className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <EditGradeSize
              submitGradesize={handleOnSubmit}
              formData={formDataEditGradeSize}
              formErrors={formErrorsEditGradeSize}
              onChangeFormInput={onChangeFormInputEditGradeSize}
              onClose={handleOnclose}
              currentSize={
                grade[editingGradeSize.gradeIndex]?.gradeSizes?.[
                  editingGradeSize.sizeIndex
                ]
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ValuesVariations;
